/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useMemo, useState } from "react";
import { Else, If, Then, When } from "react-if";
import { Box, Text, theme } from "@nordcloud/gnui";
import {
  SearchSortField,
  SearchSortOrder,
  SearchV2Filter,
} from "~/generated/graphql";
import {
  BreadcrumbsBox,
  ErrorWrap,
  EstatePeriod,
  LoaderWrap,
  Pagination,
  ReactTable,
} from "~/components";
import { ERROR_COLORS } from "~/constants";
import {
  estateV2FilterFields,
  useCurrency,
  useFilteredQueryState,
  useEstateSortV2,
  useEstateRecordsV2,
  useQueryState,
} from "~/hooks";
import { showError } from "~/services/toast";
import { isEmpty, isNotEmpty } from "~/tools";
import {
  EstateRecordsLoader,
  FiltersProp,
  ParametersBar,
  EstateRecordsChartView,
  useEstateChartParameters,
  EstateChartsQueryState,
  EstateGroupBy,
} from "./components";
import { EstateListColumns } from "./EstateList";
import { convertTags, generateNewRowData, getMultiResources } from "./helpers";

const BREADCRUMBS = [
  {
    value: "estate-records",
    label: "Estate Records",
  },
];

const BREADCRUMBS_TAGS = [
  {
    color: "notification",
    text: "Beta",
  },
];

type Props = {
  envNid?: string;
};

type SortingProp = {
  field?: SearchSortField;
  order?: SearchSortOrder;
};

type EstateQueryState = FiltersProp &
  SearchV2Filter &
  SortingProp & {
    page: number;
    limit: number;
    query: string;
  };

export function EstateRecordsPage({ envNid }: Props) {
  const {
    state: { groupBy },
  } = useQueryState<EstateChartsQueryState>();

  const activeChart = groupBy ?? EstateGroupBy.NONE;

  const {
    period,
    range,
    granularity,
    handlePeriodChange,
    search,
    setSearch,
    handleGranularityChange,
  } = useEstateChartParameters();

  const { currency } = useCurrency();
  const { state, updateQueryState } =
    useFilteredQueryState(estateV2FilterFields);
  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const { page, limit, query, field, order, ...restFilters }: EstateQueryState =
    state;

  const { sortField, sortDirection, onSortSelect, onSortOrderChange } =
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    useEstateSortV2({ field, order, updateQueryState });

  const estateFilters = {
    filter: {
      ...restFilters,
      metadata: convertTags(restFilters.metadata),
      tags: convertTags(restFilters.tags),
    },
    query,
    sort: {
      field: sortField,
      order: sortDirection,
    },
  };

  const { data, loading, error, refetch } = useEstateRecordsV2(
    {
      variables: {
        page,
        limit,
        ...estateFilters,
      },
    },
    showError
  );

  const [selectedResources, setSelectedResources] = useState<string[]>([]);

  const rows = useMemo(
    () =>
      data?.resources?.map((res) => ({
        ...generateNewRowData(res, currency, envNid),
      })) ?? [],
    [data, currency, envNid]
  );

  const hasResources = isNotEmpty(rows);
  const estateCount = data?.count ?? 0;
  const showPagination = estateCount > 0;

  const columns = useMemo(() => EstateListColumns, []);

  const resources = useMemo(
    () => getMultiResources(selectedResources, data?.resources),
    [selectedResources, data?.resources]
  );

  const selectedRowsHandler = useCallback(
    (items: string[]) => {
      setSelectedResources(
        items.filter((item) =>
          data?.resources
            ?.filter((row) => row.__typename !== "CostResource")
            .map((row) => row.id)
            .includes(item)
        )
      );
    },
    [setSelectedResources, data?.resources]
  );

  const isEstateRecordsVisible =
    activeChart === EstateGroupBy.NONE && period !== EstatePeriod.RANGE;

  return (
    <div>
      <BreadcrumbsBox
        title="Estate Records"
        labels={BREADCRUMBS}
        tags={BREADCRUMBS_TAGS}
      />
      <Box spacing="spacing04">
        <EstateRecordsChartView
          period={period}
          range={range}
          granularity={granularity}
          estateFilters={estateFilters}
          restFilters={restFilters}
          disableDailyView={false}
          isCostLoading={loading}
          currentMonthTotal={data?.cost?.currentMonthTotal}
          estateRecordsCount={data?.count ?? 0}
          monthlySavings={data?.savingsAmount?.total ?? ""}
          activeChart={activeChart}
          handleFilters={(newFilters) =>
            refetch({ variables: { filter: newFilters } })
          }
          onPeriodChange={handlePeriodChange}
          onGranularityChange={handleGranularityChange}
        />
        <When condition={isEstateRecordsVisible}>
          <ParametersBar
            disabledAttachment={isEmpty(selectedResources)}
            search={search}
            setSearch={setSearch}
            sortDirection={sortDirection}
            sortField={sortField}
            resources={resources}
            refresh={refetch}
            filters={estateFilters}
            recordsCount={data?.count ?? 0}
            onSortOrderChange={onSortOrderChange}
            onSortSelect={onSortSelect}
          />
          <ErrorWrap
            error={error}
            status={
              hasResources ? ERROR_COLORS.notification : ERROR_COLORS.danger
            }
          >
            <LoaderWrap loading={loading} Component={EstateRecordsLoader}>
              <If condition={hasResources}>
                <Then>
                  <ReactTable
                    columns={columns}
                    data={rows}
                    sort={false}
                    headerBackground={theme.color.background.ui03}
                    deselectRows={isEmpty(selectedResources ?? [])}
                    onSelectedRowsChange={selectedRowsHandler}
                  />
                  <When condition={showPagination}>
                    <Pagination count={estateCount} />
                  </When>
                </Then>
                <Else>
                  <Text
                    paddingTop={theme.spacing.spacing04}
                    paddingBottom={theme.spacing.spacing04}
                  >
                    No resources matching selected criteria
                  </Text>
                </Else>
              </If>
            </LoaderWrap>
          </ErrorWrap>
        </When>
      </Box>
    </div>
  );
}
